import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { type Build, cles, usePublication } from "../api/hooks";

/**
 * Publication du site.
 *
 * Deux principes gouvernent cet écran :
 *
 * 1. **Montrer les manquements AVANT de lancer le build.** Le pipeline refuse
 *    une publication dont le site généré ne passe pas les contrôles éditoriaux.
 *    Laisser l'utilisateur découvrir ce refus après coup lui ferait attendre
 *    pour rien et lui donnerait le sentiment d'un outil capricieux.
 *
 * 2. **Après un échec, dire que le site en ligne est intact.** C'est la seule
 *    information dont il a besoin à cet instant : la bascule est atomique, la
 *    version publiée n'a jamais été touchée. Sans ce rappel, il croira avoir
 *    cassé le site.
 */

type RapportPage = {
  type: string;
  chemin: string;
  titre: string;
  publiee: boolean;
  mots: number;
  nombreFaq: number;
  manquements: { code: string; message: string }[];
};

function useQualite() {
  return useQuery({
    queryKey: ["contenu", "qualite"],
    queryFn: () => api.get<{ pages: RapportPage[] }>("/api/contenu/qualite"),
  });
}

function dureeLisible(build: Build): string {
  if (build.termineA === null) return "en cours";
  const debut = Date.parse(build.demarreA.replace(" ", "T"));
  const fin = Date.parse(build.termineA.replace(" ", "T"));
  if (Number.isNaN(debut) || Number.isNaN(fin)) return "—";
  const secondes = Math.max(0, Math.round((fin - debut) / 1000));
  return secondes < 60 ? `${secondes} s` : `${Math.floor(secondes / 60)} min ${secondes % 60} s`;
}

const LIBELLES_STATUT: Record<Build["statut"], string> = {
  en_cours: "En cours",
  succes: "Publié",
  echec: "Refusé",
};

const COULEURS_STATUT: Record<Build["statut"], "info" | "success" | "error"> = {
  en_cours: "info",
  succes: "success",
  echec: "error",
};

export function Publication() {
  const client = useQueryClient();
  const etat = usePublication();
  const qualite = useQualite();

  const [journal, setJournal] = useState<string[]>([]);
  const [confirmationForcer, setConfirmationForcer] = useState(false);
  const finJournal = useRef<HTMLDivElement | null>(null);

  const enCours = etat.data?.enCours ?? null;
  const dernier = etat.data?.builds[0] ?? null;

  const pagesFautives = (qualite.data?.pages ?? []).filter(
    (page) => page.publiee && page.manquements.length > 0,
  );

  const publier = useMutation({
    mutationFn: (forcer: boolean) =>
      api.post<{ buildId: number; statut: string }>("/api/publication", { forcer }),
    onMutate: () => setJournal([]),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: cles.publication });
    },
  });

  // Flux d'avancement. Ouvert seulement pendant un build : une connexion
  // permanente maintiendrait une requête ouverte pour rien le reste du temps.
  useEffect(() => {
    if (enCours === null) return;

    const source = new EventSource("/api/publication/flux", { withCredentials: true });

    source.onmessage = (evenement) => {
      try {
        const donnees = JSON.parse(evenement.data) as { type: string; ligne?: string };
        if (donnees.type === "log" && donnees.ligne !== undefined) {
          setJournal((lignes) => [...lignes, donnees.ligne as string]);
        }
        if (donnees.type === "fin") {
          client.invalidateQueries({ queryKey: cles.publication });
          client.invalidateQueries({ queryKey: ["contenu", "qualite"] });
        }
      } catch {
        // Une trame illisible ne doit pas interrompre le suivi du build.
      }
    };

    // Le navigateur reconnecte tout seul ; on ne referme que sur démontage.
    return () => source.close();
  }, [enCours, client]);

  useEffect(() => {
    finJournal.current?.scrollIntoView({ block: "end" });
  }, []);

  const publicationEnCours = enCours !== null || publier.isPending;

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1">
        Publication
      </Typography>

      {/* --- Qualité éditoriale, avant le bouton et non après ------------- */}
      {qualite.isPending ? (
        <LinearProgress />
      ) : pagesFautives.length === 0 ? (
        <Alert severity="success" icon={<CheckCircleIcon />}>
          Toutes les pages publiées respectent les règles de contenu.
        </Alert>
      ) : (
        <Alert severity="warning">
          <AlertTitle>
            {pagesFautives.length === 1
              ? "Une page publiée ne respecte pas les règles de contenu"
              : `${pagesFautives.length} pages publiées ne respectent pas les règles de contenu`}
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            La publication sera refusée tant que ces manquements subsistent. Le site actuellement en
            ligne n'est pas affecté.
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {pagesFautives.map((page) => (
              <Box key={page.chemin}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {page.titre}{" "}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {page.chemin}
                  </Typography>
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                  {page.manquements.map((manquement) => (
                    <Typography key={manquement.code} component="li" variant="body2">
                      {manquement.message}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}

      {/* --- Résultat du dernier build ------------------------------------ */}
      {dernier?.statut === "echec" && enCours === null && (
        <Alert severity="error" icon={<ErrorOutlineIcon />}>
          <AlertTitle>La dernière publication a été refusée</AlertTitle>
          {/* L'information qui compte à cet instant précis. */}
          <strong>Le site en ligne est intact</strong> : il n'a jamais été remplacé. Corrigez les
          points signalés, puis relancez la publication.
        </Alert>
      )}

      {/* --- Action -------------------------------------------------------- */}
      <Paper sx={{ p: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: "center" }}>
          <Button
            variant="contained"
            size="large"
            startIcon={publicationEnCours ? <CircularProgress size={18} /> : <CloudUploadIcon />}
            disabled={publicationEnCours}
            onClick={() => publier.mutate(false)}
          >
            {publicationEnCours ? "Publication en cours…" : "Publier le site"}
          </Button>

          {pagesFautives.length > 0 && (
            <Button
              variant="outlined"
              color="warning"
              disabled={publicationEnCours}
              onClick={() => setConfirmationForcer(true)}
            >
              Publier malgré tout
            </Button>
          )}

          <Typography variant="body2" color="text.secondary">
            Régénère le site public à partir du contenu enregistré.
          </Typography>
        </Stack>

        {journal.length > 0 && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              maxHeight: 320,
              overflow: "auto",
              bgcolor: "grey.900",
              color: "grey.100",
              borderRadius: 1,
              fontFamily: "ui-monospace, monospace",
              fontSize: 13,
              whiteSpace: "pre-wrap",
            }}
          >
            {journal.map((ligne, index) => (
              // Le journal est un flux ordonné, jamais réordonné : l'index est
              // ici une clé stable, contrairement au cas d'une liste éditable.
              // biome-ignore lint/suspicious/noArrayIndexKey: flux append-only
              <div key={index}>{ligne}</div>
            ))}
            <div ref={finJournal} />
          </Box>
        )}
      </Paper>

      {/* --- Journal des publications -------------------------------------- */}
      <Paper>
        <Typography variant="h6" component="h2" sx={{ p: 2, pb: 1 }}>
          Publications récentes
        </Typography>
        <Divider />
        {etat.isPending ? (
          <LinearProgress />
        ) : (etat.data?.builds.length ?? 0) === 0 ? (
          <Typography sx={{ p: 2 }} color="text.secondary">
            Le site n'a pas encore été publié depuis l'administration.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lancée le</TableCell>
                <TableCell>Durée</TableCell>
                <TableCell>Résultat</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {etat.data?.builds.map((build) => (
                <TableRow key={build.id}>
                  <TableCell>{build.demarreA}</TableCell>
                  <TableCell>{dureeLisible(build)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={LIBELLES_STATUT[build.statut]}
                      color={COULEURS_STATUT[build.statut]}
                      variant={build.statut === "succes" ? "filled" : "outlined"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* --- Détail d'un refus --------------------------------------------- */}
      {dernier?.statut === "echec" && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Journal de la dernière publication refusée</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <JournalBuild id={dernier.id} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* --- Confirmation du contournement ---------------------------------- */}
      <Dialog open={confirmationForcer} onClose={() => setConfirmationForcer(false)}>
        <DialogTitle>Publier sans contrôle de qualité ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Les pages signalées partiront en ligne en l'état. Elles seront moins bien référencées,
            et les moteurs génératifs ne les citeront pas.
            <br />
            <br />
            Ce contournement est enregistré dans le journal de publication.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationForcer(false)}>Annuler</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              setConfirmationForcer(false);
              publier.mutate(true);
            }}
          >
            Publier malgré tout
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/** Journal d'un build passé, chargé à la demande : il peut être volumineux. */
function JournalBuild({ id }: { id: number }) {
  const build = useQuery({
    queryKey: ["publication", "build", id],
    queryFn: () => api.get<{ build: Build & { log: string } }>(`/api/publication/${id}`),
  });

  if (build.isPending) return <LinearProgress />;
  if (build.isError) return <Alert severity="error">Journal indisponible.</Alert>;

  return (
    <Box
      sx={{
        p: 2,
        maxHeight: 400,
        overflow: "auto",
        bgcolor: "grey.900",
        color: "grey.100",
        borderRadius: 1,
        fontFamily: "ui-monospace, monospace",
        fontSize: 13,
        whiteSpace: "pre-wrap",
      }}
    >
      {build.data?.build.log || "Journal vide."}
    </Box>
  );
}
